import {
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Paper,
    Link,
    Box,
} from "@mui/material";
import { useAppContext } from "../../context/AppContext";
import { DataTableProps } from "../../types";
import { truncateWithEllipsis } from "../../utils/tableDisplayUtils";

function formatDate(isoString: string): string {
    const date = new Date(isoString);
    const day = String(date.getDate()).padStart(2, '0');
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const year = date.getFullYear();
    return `${day}/${month}/${year}`;
}


export default function DataTable({ profileData }: DataTableProps) {
    const { setProfileId, setCurrentView, setProfileImageUrl, setOutputScore, getTranslation } = useAppContext();
    return (
        <Box sx={{ width: "100%", overflowX: "hidden" }}>
            <TableContainer 
                component={Paper} 
                sx={{ 
                    borderRadius: "10px", 
                    boxShadow: 0, 
                    backgroundColor: "var(--color-background)",
                    minHeight: "300px",
                    maxHeight: "300px",
                    overflowY: "auto",
                    position: "relative",
                }}
            >
                <Table sx={{ minWidth: { xs: "auto", md: "auto" }, backgroundColor: "var(--color-background)" }}>
                    <TableHead
                        sx={{
                            position: "sticky",
                            top: 0,
                            zIndex: 9,
                        }}
                    >
                        <TableRow sx={{ backgroundColor: "#DBDBDA" }}>
                            {[
                                getTranslation("Txt487", "Date"), 
                                getTranslation("Txt489", "Name"), 
                                getTranslation("Txt491", "Score"), 
                                getTranslation("Txt301"), 
                                getTranslation("Txt296"), 
                                getTranslation("Txt283")
                            ].map((header, index) => (
                                <TableCell 
                                    key={header} 
                                    sx={{
                                        ...headerStyle,
                                        display: { xs: index < 2 || index === 5 ? 'table-cell' : 'none', md: 'table-cell' }
                                    }}
                                >
                                    {header}
                                </TableCell>
                            ))}
                        </TableRow>
                    </TableHead>

                    <TableBody>
                        {profileData.map((row, index) => {
                            const fullName =
                                row.first_name && row.last_name
                                    ? `${row.first_name} ${row.last_name}`.trim()
                                    : "";
                            const truncatedName =
                                fullName.length > 0
                                    ? truncateWithEllipsis(fullName)
                                    : "—";
                            return (
                            <TableRow
                                key={index}
                                sx={{
                                    "&:nth-of-type(even)": { backgroundColor: "var(--color-background)" },
                                    borderBottom: "none",
                                }}  >
                                <TableCell sx={{
                                    ...cellStyle,
                                    display: { xs: 'table-cell', md: 'table-cell' }
                                }}>{row.createdAt ? formatDate(row.createdAt as string) : 'N/A'}</TableCell>
                                <TableCell sx={{ 
                                    ...cellStyle, 
                                    display: "flex", 
                                    alignItems: "center", 
                                    justifyContent: "flex-start", 
                                    gap: "8px" 
                                }}>
                                    <img
                                        crossOrigin="anonymous"
                                        src={row.input_image_url}
                                        alt={row.first_name && row.last_name ? `${row.first_name} ${row.last_name}` : row.email || 'User'}
                                        style={{
                                            width: "24px",
                                            height: "24px",
                                            borderRadius: "50%",
                                            objectFit: "cover"
                                        }}
                                    />
                                    <span style={{ textAlign: "center" }} title={fullName || undefined}>
                                        {truncatedName}
                                    </span>
                                </TableCell>
                                <TableCell sx={{
                                    ...cellStyle,
                                    display: { xs: 'none', md: 'table-cell' }
                                }}>
                                    {(() => {
                                        const overallScoreObj = row.output_score?.find(
                                            score => score.concern === 'skin_health'
                                        );
                                        return overallScoreObj ? overallScoreObj.score : 'N/A';
                                    })()}
                                </TableCell>
                                <TableCell sx={{
                                    ...cellStyle,
                                    display: { xs: 'none', md: 'table-cell' }
                                }}>
                                    {(() => {
                                        const oilinessScore = row.output_score?.find(
                                            score => score.concern === 'shine'
                                        );
                                        return oilinessScore ? oilinessScore.score : 'N/A';
                                    })()}
                                </TableCell>
                                <TableCell sx={{
                                    ...cellStyle,
                                    display: { xs: 'none', md: 'table-cell' }
                                }}>
                                    {(() => {
                                        const darkCirclesScore = row.output_score?.find(
                                            score => score.concern === 'dark_circle'
                                        );
                                        return darkCirclesScore ? darkCirclesScore.score : 'N/A';
                                    })()}
                                </TableCell>
                                <TableCell sx={{
                                    ...cellStyle,
                                    display: { xs: 'table-cell', md: 'table-cell' }
                                }}>
                                    <Box
                                        sx={{
                                            "&:hover": { 
                                                transform: "scale(1.05)",
                                                transition: "transform 0.2s ease-in-out"
                                            }
                                        }}
                                    >
                                        <Link
                                            onClick={(event) => {
                                                event.preventDefault();
                                                setProfileId(row._id || '');
                                                setProfileImageUrl(row.input_image_url || null);
                                                // Pass output_score to context for MoreUserDetails
                                                if (row.output_score) {
                                                    setOutputScore(row.output_score);
                                                }
                                                setCurrentView('MoreUserDetails');
                                            }}
                                            href="#" 
                                            sx={{ 
                                                color: "var(--color-text-primary)", 
                                                fontWeight: 500, 
                                                textDecoration: "underline",
                                                display: "inline-block"
                                            }}
                                        >
                                            {getTranslation("Txt283")}
                                        </Link>
                                    </Box>
                                </TableCell>
                            </TableRow>
                            );
                        })}
                    </TableBody>
                </Table>
            </TableContainer>
        </Box>
    );
}

const headerStyle = {
    fontWeight: 700,
    fontSize: { xs: "12px", md: "14px" },
    textAlign: "left",
    color: "var(--color-text-primary)",
    backgroundColor: "#DBDBDA",
    lineHeight: "20px",
    padding: "20px 20px 8px 20px"
};

const cellStyle = {
    fontSize: { xs: "14px", md: "16px" },
    color: "var(--color-text-primary)",
    fontFamily: "HerbalifeFont",
    fontWeight: 400,
    textAlign: "left",
    margin: "auto",
    lineHeight: "20px",
    padding: "10px 20px 10px 20px",
    borderBottom: "none",
};
